import { useMemo } from 'react';
import { safeGetJSON } from '@/lib/safeStorage';
import { rooms } from '@/data/rooms';

interface VisitLog {
  [roomId: string]: { count: number; lastVisit: string };
}

export const useBehaviorReflection = () => {
  const insight = useMemo(() => {
    const visitLog = safeGetJSON<VisitLog>('human-rooms-visit-log', {});
    const entries = Object.entries(visitLog);
    if (entries.length < 3) return null;

    // Analyze category patterns
    const categoryCounts: Record<string, number> = {};
    entries.forEach(([roomId, data]) => {
      const room = rooms.find(r => r.id === roomId);
      if (room) {
        categoryCounts[room.category] = (categoryCounts[room.category] || 0) + data.count;
      }
    });

    const topCategory = Object.entries(categoryCounts)
      .sort(([, a], [, b]) => b - a)[0];

    if (!topCategory) return null;

    // Analyze time patterns
    const hours = entries
      .map(([, data]) => new Date(data.lastVisit).getHours())
      .filter(h => !isNaN(h));
    
    const avgHour = hours.length > 0 
      ? Math.round(hours.reduce((a, b) => a + b, 0) / hours.length) 
      : 12;

    const timeOfDay = avgHour < 6 ? 'late_night' : avgHour < 12 ? 'morning' : avgHour < 18 ? 'afternoon' : 'evening';

    const categoryLabels: Record<string, string> = {
      identity: 'identity',
      truth: 'truth',
      time: 'time',
      mortality: 'mortality',
      nothing: 'stillness',
      night: 'darkness',
      meta: 'patterns',
      scary: 'fear',
      games: 'play',
      experimental: 'experiments',
      daily: 'ritual',
      secret: 'secrets',
    };

    const categoryLabel = categoryLabels[topCategory[0]] || topCategory[0];
    const totalVisits = entries.reduce((sum, [, d]) => sum + d.count, 0);

    return {
      topCategory: categoryLabel,
      topCategoryCount: topCategory[1],
      timeOfDay,
      totalVisits,
      uniqueRooms: entries.length,
    };
  }, []);

  return { insight };
};
